import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import styled from '@emotion/styled';
import SideBarContainer from 'Containers/SideBarContainer';
import useTranslation from 'Hooks/useTranslation';
import { useHasPermission } from 'Utils/permissions';
import { settingsTheme } from '../theme';
import { Instructor, InstructorDraft, InstructorStatus, InstructorType } from './data/instructors';
import { useInstructors, InstructorFilter } from './hooks/useInstructors';
import {
  InstructorsHeader,
  InstructorsTable,
  InstructorFormDrawer,
  DeleteConfirmDialog,
  SearchBox,
  SegmentedTabs,
  TabOption,
  InstructorFormLabels,
  Pagination,
} from './components';

const PageBody = styled.div({
  flex: 1,
  padding: '34px 40px 64px',
});

const Toolbar = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 20,
  marginTop: 26,
  flexWrap: 'wrap',
});

const LoadingState = styled.div({
  marginTop: 40,
  fontFamily: settingsTheme.font,
  fontSize: 14.5,
  color: settingsTheme.color.muted,
});

/**
 * Página de gestión de instructores (detrás de la tarjeta "Instructores" del
 * hub de Configuración). Orquesta el estado del catálogo con los organismos de
 * cabecera, tabla, panel de formulario y diálogo de borrado.
 */
export default function Instructors() {
  const history = useHistory();
  const { t2 } = useTranslation('Settings');
  const { instructors, counts, pagination, page, goToPage, loading, search, setSearch, filter, setFilter, create, update, remove } =
    useInstructors();

  // Permiso comodín `instructor.*`: cubre crear, editar y eliminar.
  const canManage = useHasPermission('instructor.create');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Instructor | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Instructor | null>(null);

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (id: string) => {
    setEditing(instructors.find(x => x.id === id) || null);
    setFormOpen(true);
  };

  const submitForm = async (draft: InstructorDraft) => {
    const result = editing ? await update(editing.id, draft) : await create(draft);
    if (result.ok) {
      setFormOpen(false);
      toast.success(editing ? t2('toastUpdated') : t2('toastCreated'));
    } else if (!result.emailTaken) {
      // El correo duplicado lo muestra el propio campo; el resto se avisa aquí.
      toast.error(result.message || t2('toastError'));
    }
    return result;
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const result = await remove(deleteTarget.id);
    setDeleteTarget(null);
    if (result.ok) toast.success(t2('toastDeleted'));
    else toast.error(result.message || t2('toastError'));
  };

  const typeLabel = (type: InstructorType) => (type === 'external' ? t2('typeExternal') : t2('typeInternal'));
  const statusLabel = (status: InstructorStatus) => (status === 'active' ? t2('statusActive') : t2('statusInactive'));

  const tabs: TabOption<InstructorFilter>[] = [
    { value: 'all', label: t2('tabAll'), count: counts.all },
    { value: 'active', label: t2('tabActive'), count: counts.active },
    { value: 'inactive', label: t2('tabInactive'), count: counts.inactive },
  ];

  const formLabels: InstructorFormLabels = {
    title: editing ? t2('formEditTitle') : t2('formAddTitle'),
    subtitle: t2('formSubtitle'),
    close: t2('close'),
    name: t2('fieldName'),
    namePlaceholder: t2('namePlaceholder'),
    email: t2('fieldEmail'),
    emailPlaceholder: t2('emailPlaceholder'),
    phone: t2('fieldPhone'),
    phonePlaceholder: t2('phonePlaceholder'),
    organization: t2('fieldOrganization'),
    organizationPlaceholder: t2('organizationPlaceholder'),
    status: t2('fieldStatus'),
    statusActive: t2('statusActive'),
    statusInactive: t2('statusInactive'),
    cancel: t2('cancel'),
    save: editing ? t2('saveEdit') : t2('saveAdd'),
  };

  return (
    <SideBarContainer navBar={false}>
      <PageBody>
        <InstructorsHeader
          breadcrumbParent={t2('title')}
          breadcrumbCurrent={t2('instructorsTitle')}
          onBack={() => history.push('/configuracion')}
          title={t2('instructorsTitle')}
          subtitle={t2('instructorsSubtitle', [String(counts.all), String(counts.active), String(counts.inactive)])}
          addLabel={t2('addInstructor')}
          onAdd={openAdd}
          canAdd={canManage}
        />

        <Toolbar>
          <SearchBox value={search} placeholder={t2('searchPlaceholder')} onChange={setSearch} />
          <SegmentedTabs<InstructorFilter> options={tabs} value={filter} onChange={setFilter} />
        </Toolbar>

        {loading && instructors.length === 0 ? (
          <LoadingState>{t2('loading')}</LoadingState>
        ) : (
          <>
            <InstructorsTable
              rows={instructors}
              canManage={canManage}
              columnLabels={{
                instructor: t2('colInstructor'),
                organization: t2('colOrganization'),
                type: t2('colType'),
                status: t2('colStatus'),
              }}
              typeLabel={typeLabel}
              statusLabel={statusLabel}
              editLabel={t2('edit')}
              deleteLabel={t2('delete')}
              emptyMessage={t2('emptyMessage')}
              onEdit={openEdit}
              onDelete={id => setDeleteTarget(instructors.find(x => x.id === id) || null)}
            />
            {pagination && pagination.totalPages > 1 ? (
              <Pagination
                page={page}
                pageSize={pagination.pageSize}
                totalPages={pagination.totalPages}
                totalItems={pagination.totalItems}
                onChange={goToPage}
                labels={{
                  showing: t2('paginationShowing'),
                  of: t2('paginationOf'),
                  page: t2('paginationPage'),
                  prev: t2('paginationPrev'),
                  next: t2('paginationNext'),
                }}
              />
            ) : null}
          </>
        )}
      </PageBody>

      <InstructorFormDrawer
        open={formOpen}
        editing={editing}
        labels={formLabels}
        onClose={() => setFormOpen(false)}
        onSubmit={submitForm}
      />

      <DeleteConfirmDialog
        open={deleteTarget !== null}
        title={t2('deleteTitle')}
        description={t2('deleteMessage', [deleteTarget?.name || ''])}
        cancelLabel={t2('deleteCancel')}
        confirmLabel={t2('deleteConfirm')}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </SideBarContainer>
  );
}
